
import { CONFERENCE_JOINED, CONFERENCE_LEFT, SET_PASSWORD } from '../base/conference';
import { ReducerRegistry } from '../base/redux';

import {
    DISABLE_LOBBY_SOUND,
    ENABLE_LOBBY,
    HANDLE_SOUND,
    KNOCKING_PARTICIPANT_ARRIVED_OR_UPDATED,
    KNOCKING_PARTICIPANT_LEFT,
    OPEN_LOBBY,
    SET_KNOCKING_STATE,
    SET_LOBBY_LOADING,
    SET_LOBBY_MODE_ENABLED,
    SET_LOBBY_OVERRIDE_RECEIVED,
    SET_PASSWORD_JOIN_FAILED
} from './actionTypes';

const DEFAULT_STATE = {
    knocking: false,
    knockingParticipants: [],
    lobbyEnabled: false,
    lobbyLoading: false,
    lobbyManuallyDisabled: false,
    lobbyOverrideReceived: null,
    passwordJoinFailed: false,
    enableLobby: false,
    disabledLobbySound: false,
    lobbyScreen: {
        _isLobbyScreenVisible: false,
        _isPrejoin: false,
        _cantJoin: false
    },
    soundBool: false
};

/**
 * Reduces redux actions which affect the display of notifications.
 *
 * @param {Object} state - The current redux state.
 * @param {Object} action - The redux action to reduce.
 * @returns {Object} The next redux state which is the result of reducing the
 * specified {@code action}.
 */
ReducerRegistry.register('features/lobby', (state = DEFAULT_STATE, action) => {
    switch (action.type) {
    case CONFERENCE_JOINED:
        return {
            ...state,
            knocking: false,
            passwordJoinFailed: false
        };
    case CONFERENCE_LEFT:
        return {
            ...state,
            knocking: false,
            passwordJoinFailed: false,
            lobbyManuallyDisabled: false,
            lobbyOverrideReceived: null
        };
    case KNOCKING_PARTICIPANT_ARRIVED_OR_UPDATED:
        return _knockingParticipantArrivedOrUpdated(action.participant, state);
    case KNOCKING_PARTICIPANT_LEFT:
        return {
            ...state,
            knockingParticipants: state.knockingParticipants.filter(p => p.id !== action.id)
        };
    case SET_KNOCKING_STATE:
        return {
            ...state,
            knocking: action.knocking,
            passwordJoinFailed: false
        };
    case HANDLE_SOUND:
        return {
            ...state,
            soundBool: action.soundBool
        };
    case SET_LOBBY_LOADING:
        return {
            ...state,
            lobbyLoading: action.loading
        };
    case SET_LOBBY_MODE_ENABLED: {
        const nextState = {
            ...state,
            lobbyEnabled: action.enabled
        };

        // Only a manual moderator toggle records the session override so a
        // config-driven auto-enable on (re)join does not resurrect a lobby
        // the moderator explicitly turned off.
        if (action.manual) {
            nextState.lobbyManuallyDisabled = !action.enabled;
        }

        return nextState;
    }
    case SET_LOBBY_OVERRIDE_RECEIVED:
        return {
            ...state,
            lobbyOverrideReceived: action.override
        };
    case ENABLE_LOBBY:
        return {
            ...state,
            enableLobby: action.enabledLobby
        };
    case SET_PASSWORD:
        return {
            ...state,
            passwordJoinFailed: false
        };
    case SET_PASSWORD_JOIN_FAILED:
        return {
            ...state,
            passwordJoinFailed: action.failed
        };

    case OPEN_LOBBY: {
        return {
            ...state,
            lobbyScreen: action.lobbyscreen
        };
    }
    case DISABLE_LOBBY_SOUND: {
        const nextDisabledLobbySound = typeof action.disabledLobbySound === 'boolean'
            ? action.disabledLobbySound
            : !state.disabledLobbySound;

        return {
            ...state,
            disabledLobbySound: nextDisabledLobbySound
        };
    }
    }

    return state;
});

/**
 * Stores or updates a knocking participant.
 *
 * @param {Object} participant - The arrived or updated knocking participant.
 * @param {Object} state - The current Redux state of the feature.
 * @returns {Object}
 */
function _knockingParticipantArrivedOrUpdated(participant, state) {
    let existingParticipant = state.knockingParticipants.find(p => p.id === participant.id);


    existingParticipant = {
        ...existingParticipant,
        ...participant
    };

    return {
        ...state,
        knockingParticipants: [
            ...state.knockingParticipants.filter(p => p.id !== participant.id),
            existingParticipant
        ]
    };
}
